import type { Breakpoint } from '@mui/material/styles';
import type { AppBarProps } from '@mui/material/AppBar';
import type { ToolbarProps } from '@mui/material/Toolbar';
import type { ContainerProps } from '@mui/material/Container';

import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Container from '@mui/material/Container';
import { useTheme } from '@mui/material/styles';
import { useScrollOffSetTop } from '../hooks/use-scroll-offset-top';
import { varAlpha } from '../theme/styles';
import { layoutClasses } from '../classes';

// ----------------------------------------------------------------------

export type HeaderSectionProps = AppBarProps & {
  layoutQuery: Breakpoint;
  disableOffset?: boolean;
  disableElevation?: boolean;
  slots?: {
    leftArea?: React.ReactNode;
    rightArea?: React.ReactNode;
    topArea?: React.ReactNode;
    centerArea?: React.ReactNode;
    bottomArea?: React.ReactNode;
  };
  slotProps?: {
    toolbar?: ToolbarProps;
    container?: ContainerProps;
  };
};

export function HeaderSection({
  sx,
  slots,
  slotProps,
  disableOffset,
  disableElevation,
  layoutQuery = 'md',
  ...other
}: HeaderSectionProps) {
  const theme = useTheme();

  const { offsetTop } = useScrollOffSetTop();

  const toolbarStyles = {
    default: {
      minHeight: 'auto',
      height: 'var(--layout-header-mobile-height)',
      transition: theme.transitions.create(['height', 'background-color'], {
        easing: theme.transitions.easing.easeInOut,
        duration: theme.transitions.duration.shorter,
      }),
      [theme.breakpoints.up('sm')]: {
        minHeight: 'auto',
      },
      [theme.breakpoints.up(layoutQuery)]: {
        height: 'var(--layout-header-desktop-height)',
      },
    },
    offset: {
      // Solid nav color when scrolled. Do not use backdrop-blur here: slotProps only override
      // `bgcolor`, so blur + grey main surface behind the sticky bar read as a grey header strip.
      bgcolor: 'var(--layout-nav-bg)',
      backdropFilter: 'none',
      WebkitBackdropFilter: 'none',
    },
  };

  return (
    <AppBar
      position="sticky"
      className={layoutClasses.header}
      // `transparent` forces `backgroundColor: transparent` in MUI's AppBar (CssVars path), so the
      // page default (#F7F8FA) reads through even when `sx` sets a fill. `inherit` keeps Paper usable.
      color="inherit"
      elevation={0}
      sx={{
        zIndex: 'var(--layout-header-zIndex)',
        boxShadow: 'none',
        backgroundImage: 'none',
        borderRadius: 0,
        borderBottom: (theme) => `1px solid ${varAlpha(theme.vars.palette.grey['500Channel'], 0.12)}`,
        ...sx,
      }}
      {...other}
    >
      {slots?.topArea}

      <Toolbar
        disableGutters
        {...slotProps?.toolbar}
        sx={{
          ...toolbarStyles.default,
          ...(!disableOffset && offsetTop && toolbarStyles.offset),
          ...slotProps?.toolbar?.sx,
        }}
      >
        <Container
          {...slotProps?.container}
          sx={{
            height: 1,
            display: 'flex',
            alignItems: 'center',
            ...slotProps?.container?.sx,
          }}
        >
          {slots?.leftArea}

          {slots?.centerArea && (
            <Box sx={{ display: 'flex', flex: '1 1 auto', justifyContent: 'center' }}>
              {slots?.centerArea}
            </Box>
          )}

          {slots?.rightArea}
        </Container>
      </Toolbar>

      {slots?.bottomArea}

      {/* Flat header: ensure no elevation shadow */}
      {null}
    </AppBar>
  );
}
